/**
 * time.js
 * The API stores and returns UTC. Everything the user reads is London time.
 */

const TZ = 'Europe/London';

/**
 * Parses a timestamp as UTC even when it arrives without a zone marker
 * ("2026-09-07 19:24:13"), which JavaScript would otherwise read as local time.
 */
export function parseUtc(value) {
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return new Date(NaN);
  const hasZone = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(value.trim());
  const normalised = value.trim().replace(' ', 'T');
  return new Date(hasZone ? normalised : `${normalised}Z`);
}

/** "Mon 8 Sep, 19:30" */
export function formatLondon(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parseUtc(iso));
}

/**
 * "2026-09-22" — today in London, which is the day the website figures are
 * grouped by. The same rule as the Worker's own londonDay, so a chart drawn
 * here and a row stored there agree about which day it is.
 */
export function londonDay(date = new Date()) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(date)
      .map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** "2026-09-20" → "Sat 20 Sep", for an axis or a sentence. */
export function formatDay(day) {
  const date = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return String(day ?? '');
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  }).format(date);
}

/** Every day from one to the other, inclusive: the x-axis of a daily chart. */
export function daysBetween(from, to) {
  const out = [];
  const start = Date.parse(`${from}T12:00:00Z`);
  const end = Date.parse(`${to}T12:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return out;

  for (let t = start; t <= end; t += 86_400_000) {
    out.push(new Date(t).toISOString().slice(0, 10));
    // A window longer than a year is not something this draws.
    if (out.length > 400) break;
  }
  return out;
}

/** "19:30" */
export function formatTime(iso) {
  if (!iso) return '';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parseUtc(iso));
}

/**
 * Formats the value of a datetime-local input ("2026-09-08T19:30") for display.
 *
 * Parsed by hand rather than with `new Date()`, because that would interpret the
 * string in the browser's timezone. The value the user typed is London wall-clock
 * time by definition, so it is displayed exactly as typed.
 */
export function formatLocalInput(value) {
  if (!value) return '';
  const [date, time = '00:00'] = value.split('T');
  const [y, m, d] = date.split('-').map(Number);
  const label = new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
  return `${label}, ${time}`;
}

/** The soonest slot the picker should offer: ten minutes from now, London time. */
export function defaultScheduleValue() {
  const soon = new Date(Date.now() + 10 * 60_000);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(soon);
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/**
 * A stored UTC instant as the value a datetime-local input expects, in London
 * wall-clock time — the inverse of what the API does with `when_local`.
 */
export function toLocalInput(iso) {
  if (!iso) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(parseUtc(iso));
  const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
